import React, {
  useState,
  useRef,
  useEffect,
  useId,
  useImperativeHandle,
} from "react";
import classes from "./Collapsible.module.scss";
import { clsx } from "clsx";
import type { CollapsibleProps } from "./CollapsibleProps";

export const Collapsible: React.FC<CollapsibleProps> = ({
  children,
  className,
  threshold = 175,
  ref,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const id = useId();

  useImperativeHandle(ref, () => ({
    open: () => setIsExpanded(true),
    close: () => setIsExpanded(false),
  }));

  useEffect(() => {
    const checkContentHeight = () => {
      if (!contentRef.current) return;
      const contentHeight = contentRef.current.scrollHeight;
      setShouldShowButton(contentHeight > threshold);
    };

    checkContentHeight();
    window.addEventListener("resize", checkContentHeight);

    return () => {
      window.removeEventListener("resize", checkContentHeight);
    };
  }, [children, threshold]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={clsx(classes.collapsible, className)}>
      <div
        ref={contentRef}
        className={clsx(
          classes.content,
          isExpanded && classes.expanded,
          shouldShowButton && classes.hasToggleButton,
        )}
        style={{
          maxHeight: isExpanded ? "none" : `${threshold}px`,
          overflow: "hidden",
        }}
        id={`collapsible-content-${id}`}
        aria-expanded={isExpanded}
      >
        {children}
      </div>

      {shouldShowButton && (
        <button
          onClick={toggleExpanded}
          className={classes.toggleButton}
          type="button"
          aria-expanded={isExpanded}
          aria-controls={`collapsible-content-${id}`}
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
};
